import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { OrgProvider } from './contexts/OrgContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import { usePermissions, useOrg } from './hooks';
import SelectOrganization from './pages/SelectOrganization';
import {
  Login,
  Dashboard,
  Users,
  Profile,
  Settings,
  ZamanatForm,
  ZignalyForm,
  SystemPromptsPage
} from './pages';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import {
  LeadListPage,
  CreateLeadPage,
  LeadDetailsPage,
  ScheduleMeetingPage
} from './pages/leads';
import {
  ProposalListPage,
  DecisionEngineReviewPage,
  ProposalDetailPage,
  ProposalGeneratingPage,
  ClientProposalReviewPage
} from './pages/proposals';
import { InvestorListPage } from './pages/investors';
import {
  OrganizationListPage,
  CreateOrganizationPage,
  OrganizationDetailPage
} from './pages/organizations';
import { MeetingDetailPage, MeetingListPage } from './pages/meetings';
import { TranscriptsPage, TranscriptDetailPage } from './pages/transcripts';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import ZamanatBrainPage from './pages/ZamanatBrainPage';
import DraftReviewPage from './pages/DraftReviewPage';
import ClientProposalReviewPageV1 from './pages/proposals/ClientProposalReviewPageV1';
import ClientProposalReviewPageV3 from './pages/proposals/ClientProposalReviewPageV3';
import ClientProposalReviewPageV4 from './pages/proposals/ClientProposalReviewPageV4';
import './App.css';

// Legacy /admin-settings/prompts link. System Prompts is now super-admin-only,
// so send super admins to the dedicated page and everyone else back to Brain
// (which admins & compliance officers can still access) rather than access-denied.
const PromptsRedirect = () => {
  const { isSuperAdmin } = usePermissions();
  return <Navigate to={isSuperAdmin ? '/system-prompts' : '/brain'} replace />;
};

// Legacy /settings/organization link. That page has been retired in favor of the
// single canonical OrganizationDetailPage — send the caller to their own org there.
const OrgSettingsRedirect = () => {
  const { activeOrg } = useOrg();
  return <Navigate to={activeOrg?.id ? `/organizations/${activeOrg.id}` : '/dashboard'} replace />;
};

// /login now renders at "/" — forward location.state so ProtectedRoute's
// { from: location } survives this hop and Login can send the user back to
// the page they originally requested instead of always landing on /dashboard.
const LoginRedirect = () => {
  const location = useLocation();
  return <Navigate to="/" replace state={location.state} />;
};

function App() {
  return (
    <ThemeProvider>
    <Router>
      <AuthProvider>
        <OrgProvider>
        <ToastProvider>
          <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginRedirect />} />
          <Route path="/:orgSlug/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/:orgSlug/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/:orgSlug/reset-password" element={<ResetPassword />} />

          {/* Form Routes - Public */}
          <Route path="/forms/zamanat" element={<ZamanatForm />} />
          <Route path="/forms/zignaly" element={<ZignalyForm />} />

          {/* Client Proposal Review - Public */}
          <Route path="/client/proposals/:token" element={<ClientProposalReviewPageV4 />} />
          <Route path="/v1/client/proposals/:token" element={<ClientProposalReviewPageV1 />} />
          <Route path="/v3/client/proposals/:token" element={<ClientProposalReviewPageV3 />} />
          <Route path="/v4/client/proposals/:token" element={<ClientProposalReviewPageV4 />} />

          {/* Organization Selection */}
          <Route
            path="/select-organization"
            element={
              <ProtectedRoute requireOrg={false}>
                <SelectOrganization />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Leads Module */}
          <Route
            path="/leads"
            element={
              <ProtectedRoute anyPermissions={['leads:read', 'leads:create']}>
                <LeadListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/create"
            element={
              <ProtectedRoute requiredPermissions={['leads:create']}>
                <CreateLeadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:leadId"
            element={
              <ProtectedRoute anyPermissions={['leads:read']}>
                <LeadDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:leadId/meetings"
            element={
              <ProtectedRoute anyPermissions={['leads:read', 'meetings:create']}>
                <ScheduleMeetingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:leadId/meetings/:meetingId"
            element={
              <ProtectedRoute anyPermissions={['meetings:read']}>
                <MeetingDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:leadId/meetings/:meetingId/transcript"
            element={
              <ProtectedRoute anyPermissions={['meetings:read']}>
                <TranscriptDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Meetings Module */}
          <Route
            path="/meetings"
            element={
              <ProtectedRoute anyPermissions={['meetings:read']}>
                <MeetingListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meetings/:meetingId"
            element={
              <ProtectedRoute anyPermissions={['meetings:read']}>
                <MeetingDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Transcripts Module */}
          <Route
            path="/transcripts"
            element={
              <ProtectedRoute anyPermissions={['meetings:read']}>
                <TranscriptsPage />
              </ProtectedRoute>
            }
          />

          {/* Assets & Proposals Module */}
          <Route
            path="/proposals"
            element={
              <ProtectedRoute anyPermissions={['proposals:read', 'proposals:generate']}>
                <ProposalListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:leadId/proposal-review"
            element={
              <ProtectedRoute anyPermissions={['proposals:read', 'proposals:generate']}>
                <DecisionEngineReviewPage />
              </ProtectedRoute>
            }
          />
          {/* Backwards compatibility redirects */}
          <Route
            path="/leads/:leadId/decision-engine"
            element={
              <ProtectedRoute anyPermissions={['proposals:read', 'proposals:generate']}>
                <DecisionEngineReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:leadId/external-research"
            element={
              <ProtectedRoute anyPermissions={['proposals:read', 'proposals:generate']}>
                <DecisionEngineReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:leadId/proposals/generating"
            element={
              <ProtectedRoute anyPermissions={['proposals:read', 'proposals:generate']}>
                <ProposalGeneratingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:leadId/proposals/:proposalId"
            element={
              <ProtectedRoute anyPermissions={['proposals:read']}>
                <ProposalDetailPage />
              </ProtectedRoute>
            }
          />
          {/* Zamanat Brain (replaces standalone KB page) */}
          <Route
            path="/brain/:tab?"
            element={
              <ProtectedRoute requiredRoles={['admin', 'compliance_officer']}>
                <ZamanatBrainPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brain/drafts"
            element={
              <ProtectedRoute requiredRoles={['admin', 'compliance_officer']}>
                <DraftReviewPage />
              </ProtectedRoute>
            }
          />
          {/* Backwards compatibility redirect */}
          <Route
            path="/knowledge-base"
            element={<Navigate to="/brain" replace />}
          />

          {/* Investors Module */}
          <Route
            path="/investors"
            element={
              <ProtectedRoute>
                <InvestorListPage />
              </ProtectedRoute>
            }
          />

          {/* System Prompts - Super Admin */}
          <Route
            path="/system-prompts"
            element={
              <ProtectedRoute superAdminOnly requireOrg={false}>
                <SystemPromptsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Settings - Prompts (role-aware redirect) */}
          <Route
            path="/admin-settings/prompts"
            element={<PromptsRedirect />}
          />

          {/* Organizations Module - Super Admin */}
          <Route
            path="/organizations"
            element={
              <ProtectedRoute superAdminOnly requireOrg={false}>
                <OrganizationListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations/create"
            element={
              <ProtectedRoute superAdminOnly requireOrg={false}>
                <CreateOrganizationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations/:orgId"
            element={
              <ProtectedRoute requireOrgAdmin matchOrgIdParam>
                <OrganizationDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Users Module - Admin & Compliance Officer */}
          <Route
            path="/users"
            element={
              <ProtectedRoute requiredRoles={['admin', 'compliance_officer']}>
                <Users />
              </ProtectedRoute>
            }
          />

          {/* Legacy Organization Settings bookmark - redirect to the canonical page */}
          <Route
            path="/settings/organization"
            element={
              <ProtectedRoute requireOrgAdmin>
                <OrgSettingsRedirect />
              </ProtectedRoute>
            }
          />

          {/* Profile & Settings */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />

          {/* Root - Login */}
          <Route path="/" element={<Login />} />

          {/* 404 Not Found */}
          <Route
            path="*"
            element={
              <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <h1 className="text-6xl font-bold text-primary mb-4">404</h1>
                  <p className="text-xl text-gray-600 mb-8">Page not found</p>
                  <a
                    href="/dashboard"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary hover:bg-primary-600 transition-colors shadow-medium"
                  >
                    Go to Dashboard
                  </a>
                </div>
              </div>
            }
          />
          </Routes>
        </ToastProvider>
        </OrgProvider>
      </AuthProvider>
    </Router>
    </ThemeProvider>
  );
}

export default App;

